/**
 * Agent Dashboard — Agent list with tier badges, status indicators,
 * tier permission matrix, and agent detail panel.
 * Phase 4.7 — WQ-164
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshIcon } from '../common/Icons';
import { listAgents, getTierMatrix, type Agent, type TierMatrix } from '../../services/agentService';

const TIER_COLORS: Record<string, string> = {
  super_admin: '#dc2626',
  tenant_admin: '#ea580c',
  infrastructure: '#2563eb',
  end_user: '#22c55e',
};

const STATUS_COLORS: Record<string, string> = {
  active: '#22c55e',
  registered: '#3b82f6',
  suspended: '#eab308',
  retired: '#6b7280',
};

const formatAction = (action: string): string =>
  action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const AgentDashboard: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tierFilter, setTierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showMatrix, setShowMatrix] = useState(false);
  const [tierMatrix, setTierMatrix] = useState<TierMatrix | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusSaved, setStatusSaved] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: Record<string, string> = {};
      if (tierFilter) filters.tier = tierFilter;
      if (statusFilter) filters.status = statusFilter;
      const data = await listAgents(authFetch, filters);
      setAgents(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agents');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) fetchData();
  }, [authLoading, tierFilter, statusFilter]);

  const loadMatrix = async () => {
    if (tierMatrix) { setShowMatrix(!showMatrix); return; }
    try {
      const data = await getTierMatrix(authFetch);
      setTierMatrix(data);
      setShowMatrix(true);
    } catch {
      setError('Failed to load tier matrix');
    }
  };

  const handleStatusChange = async (agentId: string, agentName: string, currentStatus: string, newStatus: string) => {
    if (newStatus === currentStatus) return;
    if ((newStatus === 'suspended' || newStatus === 'inactive') && currentStatus === 'active') {
      if (!confirm(`This will prevent ${agentName} from processing any requests. Continue?`)) return;
    }
    setStatusSaving(true);
    try {
      await authFetch(`/api/admin/agents/${agentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      setStatusSaved(true);
      setTimeout(() => setStatusSaved(false), 2000);
      await fetchData();
      // Update selected agent to reflect new status
      setSelectedAgent(prev => prev ? { ...prev, status: newStatus } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update agent status');
    }
    setStatusSaving(false);
  };

  if (loading && agents.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading agents...</div>;
  }

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={tierFilter} onChange={e => setTierFilter(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}>
          <option value="">All Tiers</option>
          <option value="super_admin">Super Admin</option>
          <option value="tenant_admin">Tenant Admin</option>
          <option value="infrastructure">Infrastructure</option>
          <option value="end_user">End User</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="registered">Registered</option>
          <option value="suspended">Suspended</option>
          <option value="retired">Retired</option>
        </select>
        <button onClick={loadMatrix} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6,
          border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
          background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          {showMatrix ? 'Hide' : 'Show'} Tier Matrix
        </button>
        <button onClick={fetchData} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6,
          border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
          background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <RefreshIcon size={14} /> Refresh
        </button>
      </div>

      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {/* Tier Matrix Panel */}
      {showMatrix && tierMatrix && (
        <div className="glass-card" style={{ padding: 16, marginBottom: 16, overflowX: 'auto' }}>
          <h4 style={{ margin: '0 0 12px' }}>ML Action Permission Matrix</h4>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>Action</th>
                {Object.keys(tierMatrix.matrix).map(tier => (
                  <th key={tier} style={{ textAlign: 'center', padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ color: TIER_COLORS[tier] || '#888' }}>{tier}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.keys(Object.values(tierMatrix.matrix)[0] || {}).map(action => (
                <tr key={action}>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                    {formatAction(action)}
                  </td>
                  {Object.keys(tierMatrix.matrix).map(tier => (
                    <td key={tier} style={{ textAlign: 'center', padding: '4px 8px', borderBottom: '1px solid var(--border)' }}>
                      {tierMatrix.matrix[tier][action] ? '✅' : '❌'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Agent List */}
      {agents.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          No agents registered. Agents are created by the platform during service initialization.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Role</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Tier</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Status</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Essential</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {agents.map(agent => (
                <tr key={agent.id}
                  onClick={() => setSelectedAgent(selectedAgent?.id === agent.id ? null : agent)}
                  style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', background: selectedAgent?.id === agent.id ? 'var(--bg-secondary)' : 'transparent' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 600 }}>{agent.name}</td>
                  <td style={{ padding: '8px 12px' }}>{agent.agent_type}</td>
                  <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                    {agent.tier && (
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 10,
                        background: `${TIER_COLORS[agent.tier] || '#888'}20`,
                        color: TIER_COLORS[agent.tier] || '#888',
                        fontWeight: 600,
                      }}>{agent.tier}</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 10,
                      background: `${STATUS_COLORS[agent.status] || '#888'}20`,
                      color: STATUS_COLORS[agent.status] || '#888',
                    }}>{agent.status}</span>
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                    {agent.is_essential && <span style={{ color: '#22c55e' }}>✓</span>}
                  </td>
                  <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {new Date(agent.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Agent Detail Panel */}
      {selectedAgent && (
        <div className="glass-card" style={{ padding: 20, marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>{selectedAgent.name}</h3>
            <button onClick={() => setSelectedAgent(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}>×</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', fontSize: 13 }}>
            <div><strong>Role:</strong> {selectedAgent.agent_type}</div>
            <div><strong>Tier:</strong> {selectedAgent.tier || 'N/A'}</div>
            <div>
              <strong>Status:</strong>{' '}
              <select
                value={selectedAgent.status}
                onChange={e => handleStatusChange(selectedAgent.id, selectedAgent.name, selectedAgent.status, e.target.value)}
                disabled={statusSaving}
                style={{
                  padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                  border: `1px solid ${STATUS_COLORS[selectedAgent.status] || 'var(--border)'}`,
                  background: `${STATUS_COLORS[selectedAgent.status] || '#888'}10`,
                  color: STATUS_COLORS[selectedAgent.status] || 'var(--text-primary)',
                  cursor: 'pointer', marginLeft: 4,
                  appearance: 'none', paddingRight: 24,
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 6px center',
                }}
              >
                <option value="active">Active</option>
                <option value="registered">Registered</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
              {statusSaving && <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 6 }}>Saving...</span>}
              {statusSaved && <span style={{ fontSize: 11, color: '#22c55e', marginLeft: 6 }}>✓ Saved</span>}
            </div>
            <div><strong>Essential:</strong> {selectedAgent.is_essential ? 'Yes' : 'No'}</div>
            {selectedAgent.description && (
              <div style={{ gridColumn: '1/-1' }}><strong>Description:</strong> {selectedAgent.description}</div>
            )}
          </div>
          {selectedAgent.capabilities && Object.keys(selectedAgent.capabilities).length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Capabilities</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {Object.entries(selectedAgent.capabilities).map(([key, val]) => (
                  <span key={key} style={{
                    fontSize: 12, padding: '3px 10px', borderRadius: 12,
                    background: val ? '#22c55e20' : '#6b728020',
                    color: val ? '#22c55e' : '#6b7280',
                    border: `1px solid ${val ? '#22c55e40' : '#6b728040'}`,
                  }}>
                    {key.replace(/_/g, ' ')}: {val ? '✓' : '✗'}
                  </span>
                ))}
              </div>
            </div>
          )}
          {selectedAgent.ml_permissions && (selectedAgent.ml_permissions as any).allowed_actions?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>ML Permissions</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {((selectedAgent.ml_permissions as any).allowed_actions || []).map((action: string) => (
                  <span key={action} style={{
                    fontSize: 11, padding: '3px 10px', borderRadius: 12,
                    background: '#3b82f620', color: '#3b82f6',
                    border: '1px solid #3b82f640', fontFamily: 'monospace',
                  }}>
                    {action.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentDashboard;
